import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Layout } from '@lib/common';
import Icon from '@ant-design/icons';
import { useRouter } from 'next/router';
import HeaderContainer from '../../containers/test-maker-layout/header.container';
import ExploreIcon from '../../../public/icons/explore-icon';
import SpaceIcon from '../../../public/icons/space-icon';
import MyAssignmentIcon from '../../../public/icons/my-assignment-icon';
import dynamic from 'next/dynamic';
import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { FolderOutlined, FileTextOutlined, CrownOutlined, TeamOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import {
  buildAuthenticationRedirectUrl,
  buildManageSubscriptionUrl,
  hasValidAccessToken,
  isPublicTestMakerBricksPath,
} from '../../utils/auth-guard.util';
import { useHelpMenu } from '../../hooks/use-help-menu.hook';
import SiderNewDropdown from '../../containers/test-maker-layout/sider-new-dropdown.component';
import { AssignmentListSkeleton } from '../../containers/assignment/assignment-list-skeleton.component';
import { StyledTableAssignmentContainer } from '../../containers/assignment/list-assignment.container.styles';

const AssignmentListLoading = () => (
  <StyledTableAssignmentContainer>
    <AssignmentListSkeleton />
  </StyledTableAssignmentContainer>
);

const ExploreContainerDynamic = dynamic<{ selectedFolderId?: string }>(
  () => import('../../containers/explore/explore-popular-tests.container'),
  {
    ssr: false,
  },
);

const ListAssignmentContainerDynamic = dynamic(
  () => import('../../containers/assignment/list-assignment.container'),
  { loading: AssignmentListLoading },
);

const SpaceContainerDynamic = dynamic<{ selectedFolderId?: string }>(
  () => import('../../containers/space/space.container'),
  {
    ssr: false,
  },
);

const ListExaminationContainerDynamic = dynamic(
  () => import('../../containers/assignment/examination-list.container'),
  { loading: AssignmentListLoading },
);

const ClassesContainerDynamic = dynamic(
  () => import('../../containers/classes/classes.container'),
  {
    ssr: false,
  },
);

const FormCreateModalDynamic = dynamic(
  () => import('../../containers/space/form/form-create.modal'),
  { ssr: false },
);
const FolderCreateModalDynamic = dynamic(
  () => import('../../containers/space/folder/folder-create.modal'),
  { ssr: false },
);

const DashboardPage = () => {
  const { t } = useTranslation('common');
  const { t: classT } = useTranslation('class');
  const dispatch = useDispatch();
  const router = useRouter();
  const isAuthenticated = hasValidAccessToken();
  const selectedTab = (router.query.menu as string) || (isAuthenticated ? 'space' : 'explore');
  /** Avoid rendering Space / Assignments before redirect when the URL still has `menu=space` etc. */
  const effectiveTab = isAuthenticated ? selectedTab : 'explore';
  const [isOpenSiderCreateModal, setIsOpenSiderCreateModal] = useState(false);
  const [isOpenFolderCreateModal, setIsOpenFolderCreateModal] = useState(false);
  const [isOpenClassCreateModal, setIsOpenClassCreateModal] = useState(false);
  const { helpMenuItem } = useHelpMenu();

  useEffect(() => {
    if (!router.isReady || router.query.create !== 'class') return;
    setIsOpenClassCreateModal(true);
    const query = { ...router.query };
    delete query.create;
    router.replace({ pathname: '/bricks', query }, undefined, { shallow: true });
  }, [router, router.isReady, router.query.create]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (!router.query.menu) {
      router.replace(
        {
          pathname: `/bricks`,
          query: { menu: isAuthenticated ? 'space' : 'explore', level: 1 },
        },
        undefined,
        { shallow: true },
      );
    }
  }, [router.isReady, router.query.menu, isAuthenticated]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (isAuthenticated) {
      return;
    }
    if (!isPublicTestMakerBricksPath(router.asPath)) {
      window.location.href = buildAuthenticationRedirectUrl(router.asPath);
    }
  }, [router.isReady, router.asPath, isAuthenticated]);

  const handleOpenCreateFormModal = useCallback(() => {
    setIsOpenSiderCreateModal(true);
  }, []);

  const handleOpenFolderCreateModal = useCallback(() => {
    setIsOpenFolderCreateModal(true);
  }, []);

  const handleTabChange = useCallback(
    (menu: string) => {
      router.push(
        {
          pathname: `/bricks`,
          query: { menu, level: 1 },
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  const handleOpenClassCreateModal = useCallback(() => {
    setIsOpenClassCreateModal(true);
    if (selectedTab !== 'classes') {
      handleTabChange('classes');
    }
  }, [handleTabChange, selectedTab]);

  const menuItems = [
    {
      key: 'space',
      label: t('space'),
      icon: <Icon component={SpaceIcon} />,
      onClick: () => selectedTab !== 'space' && handleTabChange('space'),
    },
    {
      key: 'classes',
      label: classT('labels.classes'),
      icon: <TeamOutlined />,
      onClick: () => selectedTab !== 'classes' && handleTabChange('classes'),
    },
    {
      key: 'assignments',
      label: t('assignments'),
      icon: <Icon component={MyAssignmentIcon} />,
      children: [
        {
          key: 'practice',
          label: t('practice'),
          onClick: () => selectedTab !== 'practice' && handleTabChange('practice'),
        },
        {
          key: 'examination',
          label: t('examination'),
          onClick: () => selectedTab !== 'examination' && handleTabChange('examination'),
        },
      ],
    },
    {
      key: 'explore',
      label: t('explore'),
      icon: <Icon component={ExploreIcon} />,
      onClick: () => selectedTab !== 'explore' && handleTabChange('explore'),
    },
  ];

  const visibleMenuItems = isAuthenticated
    ? menuItems
    : menuItems.filter((item) => item.key === 'explore');

  const newMenuItems = [
    {
      key: 'create-folder',
      label: t('spaceNewFolder'),
      icon: <FolderOutlined />,
      onClick: handleOpenFolderCreateModal,
    },
    {
      key: 'create-form',
      label: t('spaceNewForm'),
      icon: <FileTextOutlined />,
      onClick: handleOpenCreateFormModal,
    },
    {
      key: 'create-class',
      label: classT('breadcrumb.classFallback'),
      icon: <TeamOutlined />,
      onClick: handleOpenClassCreateModal,
    },
  ];

  const handleNavigateToManageSubscription = useCallback(() => {
    window.open(buildManageSubscriptionUrl(), '_blank', 'noopener,noreferrer');
  }, []);

  const sidebarBottomItems = useMemo(() => {
    if (!isAuthenticated) return [helpMenuItem];

    return [
      helpMenuItem,
      {
        key: 'manage-subscription',
        label: t('manageSubscription'),
        icon: <CrownOutlined />,
        onClick: handleNavigateToManageSubscription,
      },
    ];
  }, [handleNavigateToManageSubscription, helpMenuItem, isAuthenticated, t]);

  const renderContent = useMemo(() => {
    switch (effectiveTab) {
      case 'explore':
        return <ExploreContainerDynamic />;
      case 'space':
        return <SpaceContainerDynamic />;
      case 'classes':
        return (
          <ClassesContainerDynamic
            isCreateModalOpen={isOpenClassCreateModal}
            onCreateModalOpen={handleOpenClassCreateModal}
            onCreateModalClose={() => setIsOpenClassCreateModal(false)}
          />
        );
      case 'practice':
        return <ListAssignmentContainerDynamic />;
      case 'examination':
        return <ListExaminationContainerDynamic />;
      default:
        return null;
    }
  }, [effectiveTab, handleOpenClassCreateModal, isOpenClassCreateModal]);

  return (
    <Layout>
      <Layout.Logo
        subTitle="Teacher Portal"
        logoSrc="/images/Favicon.svg"
        logoTitleSrc="/icons/digi-logo.svg"
        logoWidth={40}
        logoHeight={40}
        onLogoClick={() => handleTabChange('explore')}
      />
      <Layout.Header>
        <HeaderContainer />
      </Layout.Header>

      <Layout.Sider
        items={visibleMenuItems}
        bottomItems={sidebarBottomItems}
        defaultOpenKeys={isAuthenticated ? ['assignments'] : ['explore']}
        openKeys={isAuthenticated ? ['assignments'] : ['explore']}
        selectedKeys={[effectiveTab]}
        topContent={isAuthenticated ? <SiderNewDropdown menuItems={newMenuItems} /> : undefined}
        collapseLabel={t('collapseSidebar')}
        expandLabel={t('expandSidebar')}
      />
      <Layout.Content padding={24}>{renderContent}</Layout.Content>
      <FormCreateModalDynamic
        open={isOpenSiderCreateModal}
        onClose={() => setIsOpenSiderCreateModal(false)}
      />
      <FolderCreateModalDynamic
        open={isOpenFolderCreateModal}
        onClose={() => setIsOpenFolderCreateModal(false)}
      />
    </Layout>
  );
};
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context;

  if (locale === undefined) {
    throw new Error('locale is missing');
  }
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'explore',
        'common',
        'class',
        'assignment',
        'component',
        'form',
      ])),
    },
    revalidate: 300,
  };
};

DashboardPage.getLayout = function getLayout(page: ReactElement) {
  return page;
};

export default DashboardPage;
